import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import PageHeader from "../../../components/common/PageHeader/PageHeader";
import CloseButton2 from "../../../components/common/CloseButton/CloseButton2";
import InputBoxSmall from "../../../components/common/InputBox/InputBoxSmall";
import TextBox from "../../../components/common/TextBox/TextBox";
import InputBox from "../../../components/common/InputBox/InputBox";
import InputBoxLarge from "../../../components/common/InputBox/InputBoxLarge";
import QuestionMarkIcon from "../../../components/common/QuestionMark/QuestionMark";
import AddButton from "../../../components/common/AddButton/AddButton";
import DiscountBox from "../../../components/common/InputAndSelect/DropDownRight";
import ErrorNotification from "../../../components/common/ErrorAlert/ErrorNotification";
import { SuccessAlert } from "../../../components/common/ErrorAlert/SuccessAlert";
import ExitPageWarningPopup from "../../../components/popup/ExitPagePopup/ExitPageWarningPopup";
import InputTagsBoxSmall from "../../../components/common/InputTagsBox/SmallTagBox";
import { useTranslation } from "react-i18next";

import useCurrencyStore from "../../../store/useCurrencyStore";
import { useExitWarning } from "../../../hooks/useExitWarning";
import {
  CreateDebitNote,
  getPendingDebitNoteItems,
} from "../../../services/DB-CR-notesService";
import { GetJournals } from "../../../services/AccountsApi";
import SearchSelectBox from "../../../components/common/SelectBox/SelectBoxWithSearch";
import CloseRed from "../../../components/common/CloseButton/CloseRed";
import AddButtonTable from "../../../components/common/AddButton/addButtonTable";
import {
  calculateLineSubtotal,
  calculateDocumentTotals,
} from "../../../utils/calculations";
import { safeRound } from "../../../utils/safeRound";
import DateTimeInputField from "../../../components/common/DateTimePicker/DateTimeInputField";

const CreateDebitNotes = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = location.state || {};

  const currencySymbol = useCurrencyStore(
    (state) => state.currencySymbol.currency_symbol,
  );

  const [products, setProducts] = useState([]);
  const [remainingQuantity, setRemainingQuantity] = useState([]);
  const [debitNote, setDebitNote] = useState({});
  const [billAddress, setBillAddress] = useState(null);
  const [shipAddress, setShipAddress] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [originalProducts, setOriginalProducts] = useState([]);
  const [productQuantityMap, setProductQuantityMap] = useState(new Map());

  const { showExitPopup, handleStay, handleLeave } = useExitWarning(isDirty);
  const [journals, setJournals] = useState([]);
  const [selectedJournal, setSelectedJournal] = useState(null);
  const { t } = useTranslation();

  const discountOptions = [
    { label: "%", value: "percentage" },
    { label: currencySymbol, value: "fixed" },
  ];

  useEffect(() => {
    if (id) fetchDetails();
    fetchPurchaseJournals();
  }, [id]);

  const mapAppliedTaxesToUtilityFormat = useCallback((appliedTaxes) => {
    if (!appliedTaxes || !Array.isArray(appliedTaxes)) {
      return [];
    }

    return appliedTaxes.map((tax) => ({
      id: tax.tax,
      value: tax.tax,
      rate: parseFloat(tax.tax_rate) || 0,
      type: "Percentage",
      label: tax.tax_name || "",
    }));
  }, []);

  const createProductQuantityMap = useCallback((remainingProducts) => {
    const map = new Map();
    if (remainingProducts && remainingProducts.length > 0) {
      remainingProducts.forEach((item) => {
        if (item && item.product_id) {
          map.set(item.product_id, item.remaining_qty);
        }
      });
    }
    return map;
  }, []);

  const getRemainingQuantityForProduct = useCallback(
    (productId) => {
      return Number(productQuantityMap.get(productId) || 0);
    },
    [productQuantityMap],
  );

  async function fetchPurchaseJournals() {
    try {
      const response = await GetJournals();

      if (response?.data) {
        const purchaseJournals = response.data.filter(
          (journal) => journal.journal_type_name === "Purchase",
        );

        const journalOptions = purchaseJournals.map((journal) => ({
          id: journal.id,
          name: journal.name,
          label: journal.name,
          value: journal.id,
          journal_type: journal.journal_type_name,
          default_account: journal.default_account_name,
        }));

        setJournals(journalOptions);
      }
    } catch (error) {
      console.error("Failed to fetch journals:", error);
    }
  }

  const initializeProductCalculations = useCallback(
    (productsList, remainingProducts) => {
      if (!productsList || productsList.length === 0) {
        return [];
      }

      const quantityMap = createProductQuantityMap(remainingProducts);
      setProductQuantityMap(quantityMap);

      const processedProducts = [];

      productsList.forEach((product) => {
        const remainingQty = quantityMap.get(product.product?.id) || 0;

        if (remainingQty === 0) {
          console.log(
            `Skipping product ${product?.product?.name} - Zero remaining quantity`,
          );
          return;
        }

        const rawQty = Number(product.quantity) || 0;
        const orderedQty =
          remainingQty || Number(product.ordered_quantity) || 0;
        const validQuantity = Math.min(rawQty, orderedQty);

        const appliedTaxes = product.applied_taxes || [];
        const mappedTaxes = mapAppliedTaxesToUtilityFormat(appliedTaxes);

        const backendSubtotal = Number(product.subtotal) || 0;
        const backendQuantity = Number(product.quantity) || 1;
        const effectiveUnitPrice = backendSubtotal / backendQuantity;

        const { subtotal, tax_amount, tax_breakdown } = calculateLineSubtotal({
          quantity: validQuantity,
          unitPrice: effectiveUnitPrice,
          discount: 0,
          discountType: "percentage",
          taxes: mappedTaxes,
          availableTaxes: mappedTaxes,
        });

        processedProducts.push({
          ...product,
          quantity: validQuantity,
          subtotal: subtotal,
          tax_amount: tax_amount,
          tax_breakdown: tax_breakdown,
          applied_taxes: appliedTaxes,
          taxes: mappedTaxes,
          ordered_quantity: remainingQty,
          effective_unit_price: effectiveUnitPrice,
        });
      });

      return processedProducts;
    },
    [createProductQuantityMap, mapAppliedTaxesToUtilityFormat],
  );

  const updateDebitNoteTotals = useCallback(
    (currentProducts, originalValues = null) => {
      if (!currentProducts || currentProducts.length === 0) {
        setDebitNote((prev) => ({
          ...prev,
          untaxed_total: 0,
          tax_amount: 0,
          total_amount: 0,
          net_total_amount: 0,
        }));
        return;
      }

      const originalOrderTotal =
        originalValues?.originalOrderTotal ||
        Number(debitNote?.original_untaxed_total) ||
        0;
      const originalDiscountAmount =
        originalValues?.originalDiscountAmount ||
        Number(debitNote?.original_discount_amount) ||
        0;
      const originalDiscountPercentage =
        originalValues?.originalDiscountPercentage ||
        Number(debitNote?.original_discount_percentage) ||
        0;

      let globalDiscountAmount = 0;
      let globalDiscountType = "fixed";

      if (originalDiscountPercentage > 0) {
        globalDiscountAmount = originalDiscountPercentage;
        globalDiscountType = "percentage";
      } else if (originalDiscountAmount > 0 && originalOrderTotal > 0) {
        const currentUntaxedTotal = currentProducts.reduce((acc, product) => {
          return acc + (Number(product.subtotal) || 0);
        }, 0);
        globalDiscountAmount =
          (currentUntaxedTotal / originalOrderTotal) * originalDiscountAmount;
        globalDiscountType = "fixed";
      }

      const adjustmentAmount = Number(debitNote?.adjustment_amount) || 0;
      const adjustmentType = debitNote?.adjustment_type || "+";

      const totals = calculateDocumentTotals({
        products: currentProducts,
        globalDiscount: globalDiscountAmount,
        globalDiscountType: globalDiscountType,
        adjustmentAmount: adjustmentAmount,
        adjustmentType: adjustmentType,
        availableTaxes: [],
      });

      setDebitNote((prev) => ({
        ...prev,
        discount_amount: Math.round(totals.discount_amount * 100) / 100,
        gross_total: Math.round(totals.gross_total * 100) / 100,
        untaxed_total: Math.round(totals.untaxed_total * 100) / 100,
        tax_amount: Math.round(totals.tax_amount * 100) / 100,
        total_amount: Math.round(totals.total_amount * 100) / 100,
        net_total_amount: Math.round(totals.net_total_amount * 100) / 100,
      }));
    },
    [
      debitNote?.original_untaxed_total,
      debitNote?.original_discount_amount,
      debitNote?.original_discount_percentage,
      debitNote?.adjustment_amount,
      debitNote?.adjustment_type,
    ],
  );

  const fetchDetails = useCallback(async () => {
    try {
      console.log("Fetching debit note details");
      const response = await getPendingDebitNoteItems(id);
      const returnData = response?.purchase_return;

      const originalDiscountPercentage =
        Number(returnData?.discount_percentage) || 0;
      const originalDiscountAmount = Number(returnData?.discount_amount) || 0;
      const originalOrderTotal =
        Number(returnData?.total_amount) + originalDiscountAmount;

      setDebitNote({
        ...returnData,
        original_untaxed_total: originalOrderTotal,
        original_discount_amount: originalDiscountAmount,
        original_discount_percentage: originalDiscountPercentage,
      });

      setRemainingQuantity(response?.remaining_products || []);

      const initializedProducts = initializeProductCalculations(
        returnData?.purchase_return_products,
        response?.remaining_products,
      );
      setProducts(initializedProducts);
      setOriginalProducts(initializedProducts);

      fetchAddresses(returnData?.purchase_return_addresses || []);

      updateDebitNoteTotals(initializedProducts, {
        originalOrderTotal,
        originalDiscountAmount,
        originalDiscountPercentage,
      });
    } catch (error) {
      console.error("Failed to fetch debit note details:", error);
    }
  }, [id, initializeProductCalculations, updateDebitNoteTotals]);

  const fetchAddresses = (addresses = []) => {
    const billing = addresses.find((addr) => addr.address_type === "billing");
    const shipping = addresses.find((addr) => addr.address_type === "shipping");

    setBillAddress(billing || null);
    setShipAddress(shipping || null);
  };

  const handleFieldChange = (field, value) => {
    setIsDirty(true);

    // Clear specific field error when user starts typing
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }

    setDebitNote((prev) => ({ ...prev, [field]: value }));
  };

  const handleProductChange = useCallback(
    (index, field, value) => {
      setIsDirty(true);

      if (field !== "quantity") return;

      setProducts((prevProducts) => {
        const updatedProducts = [...prevProducts];
        const product = { ...updatedProducts[index] };

        const quantity = parseInt(value || 0, 10);
        const maxAllowedQty = getRemainingQuantityForProduct(
          product.product?.id,
        );

        if (quantity > maxAllowedQty) {
          console.warn(
            `Quantity ${quantity} exceeds allowed quantity ${maxAllowedQty}`,
          );
          return prevProducts;
        }

        const unitPrice = product.effective_unit_price || product.unit_price;

        const { subtotal, tax_amount, tax_breakdown } = calculateLineSubtotal({
          quantity: quantity,
          unitPrice: unitPrice,
          discount: 0,
          discountType: "percentage",
          taxes: product.taxes || [],
          availableTaxes: product.taxes || [],
        });

        product.quantity = quantity;
        product.subtotal = subtotal;
        product.tax_amount = tax_amount;
        product.tax_breakdown = tax_breakdown;

        updatedProducts[index] = product;

        setTimeout(() => updateDebitNoteTotals(updatedProducts), 0);

        return updatedProducts;
      });
    },
    [getRemainingQuantityForProduct, updateDebitNoteTotals],
  );

  const handleJournalChange = (e) => {
    setIsDirty(true);
    const journalId = e.target.value;
    setSelectedJournal(journalId);
    handleFieldChange("journal", journalId);
  };

  const removeProduct = useCallback(
    (indexToRemove) => {
      const updatedProducts = products.filter(
        (_, index) => index !== indexToRemove,
      );

      if (updatedProducts.length === 0) {
        setProducts([{ product: "", isNewRow: true }]);
        updateDebitNoteTotals([]);
      } else {
        setProducts(updatedProducts);
        updateDebitNoteTotals(updatedProducts);
      }
    },
    [products, updateDebitNoteTotals],
  );

  const getAvailableProductsForAdd = useCallback(() => {
    if (!productQuantityMap || !originalProducts) return [];

    const currentProductIds = products
      .filter((p) => p.product?.id)
      .map((p) => p.product.id);

    return originalProducts.filter((originalProduct) => {
      const remainingQty = productQuantityMap.get(originalProduct.product.id);
      return (
        remainingQty &&
        Number(remainingQty) > 0 &&
        !currentProductIds.includes(originalProduct.product.id)
      );
    });
  }, [productQuantityMap, originalProducts, products]);

  const canAddMoreProducts = useMemo(() => {
    return getAvailableProductsForAdd().length > 0;
  }, [getAvailableProductsForAdd]);

  const addNewProduct = useCallback(() => {
    setProducts((prev) => {
      const filteredPrev = prev.filter((p) => p.product?.id);
      return [...filteredPrev, { product: "", isNewRow: true }];
    });
  }, []);

  const handleProductSelection = useCallback(
    (index, selectedProductId) => {
      if (!selectedProductId) return;

      const selectedProduct = getAvailableProductsForAdd().find(
        (p) => p.product.id === parseInt(selectedProductId),
      );

      if (selectedProduct) {
        const remainingQty = productQuantityMap.get(selectedProduct.product.id);

        const unitPrice =
          selectedProduct.effective_unit_price || selectedProduct.unit_price;
        const appliedTaxes = selectedProduct.applied_taxes || [];
        const mappedTaxes = mapAppliedTaxesToUtilityFormat(appliedTaxes);

        const { subtotal, tax_amount, tax_breakdown } = calculateLineSubtotal({
          quantity: remainingQty,
          unitPrice: unitPrice,
          discount: 0,
          discountType: "percentage",
          taxes: mappedTaxes,
          availableTaxes: mappedTaxes,
        });

        const newProduct = {
          ...selectedProduct,
          quantity: remainingQty,
          ordered_quantity: remainingQty,
          subtotal: subtotal,
          tax_amount: tax_amount,
          tax_breakdown: tax_breakdown,
          taxes: mappedTaxes,
          isNewRow: false,
        };

        setProducts((prev) => {
          const updated = [...prev];
          updated[index] = newProduct;
          setTimeout(() => updateDebitNoteTotals(updated), 0);
          return updated;
        });
      }
    },
    [
      getAvailableProductsForAdd,
      productQuantityMap,
      mapAppliedTaxesToUtilityFormat,
      updateDebitNoteTotals,
    ],
  );

  const validateForm = () => {
    const errors = {};

    if (!debitNote?.debit_note_date) {
      errors.debit_note_date = t("Debit note date is required");
    }

    if (!id) {
      errors.purchase_return = t("Purchase return reference is required");
    }

    if (!debitNote?.vendor?.name && !debitNote?.vendor_name) {
      errors.vendor = t("Vendor is required");
    }

    if (!debitNote?.journal) {
      errors.journal = t("Please select a journal");
    }

    const validProducts = products.filter(
      (p) => p.product?.id && Number(p.quantity) > 0,
    );
    if (validProducts.length === 0) {
      errors.products = t("At least one product with quantity > 0 is required");
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAdd = async () => {
    if (!validateForm()) return;

    try {
      const payload = {
        purchase_return_id: id,
        debit_note_date:
          debitNote?.debit_note_date || new Date().toISOString().split("T")[0],
        notes: debitNote?.notes || "",
        journal: debitNote?.journal,
        products: products
          .filter((p) => p.product?.id && Number(p.quantity) > 0)
          .map((p) => ({
            product_id: p.product?.id,
            quantity: Number(p.quantity),
          })),
        discount_amount: debitNote.discount_amount || 0,
        gross_total: debitNote.gross_total || 0,
      };

      const response = await CreateDebitNote(payload);
      if (response?.data?.success) {
        SuccessAlert(
          response.data.message || t("Debit note created successfully"),
        );
        navigate(`/accounts/debit-notes/details/${response.data.data.uuid}`);
      }
    } catch (error) {
      console.error("Debit note creation failed:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to create debit note";
      setFormErrors({ submit: errorMessage });
    }
  };

  const handleClose = () => {
    navigate("/accounts/debit-notes");
  };

  return (
    <div className="whole-container">
      <ExitPageWarningPopup
        isOpen={showExitPopup}
        onClose={handleStay}
        onSave={handleLeave}
      />

      <div className="path-header">
        <div className="path-name">
          <PageHeader
            breadcrumbs={[
              { label: "Accounts" },
              { label: "Debit Notes", path: "/accounts/debit-notes" },
              { label: "Create" },
            ]}
          />
        </div>
      </div>

      <div className="page-actions-bar">
        <div className="title-heading">{t("Create Debit Note")}</div>
        <div className="page-name-rightside-icons">
          <AddButton onClick={handleAdd} />
          <CloseButton2 onClick={handleClose} />
        </div>
      </div>

      {Object.keys(formErrors).length > 0 && (
        <div style={{ marginTop: "1rem" }}>
          <ErrorNotification
            errors={formErrors}
            width="45%"
            marginBottom="0rem"
          />
        </div>
      )}

      <div className="sales-main-container">
        <div className="customer-info">
          <div className="details-container">
            <div className="customer-box" style={{ marginTop: "1.5rem" }}>
              <div className="form-row">
                <InputBoxLarge
                  label="Vendor Name"
                  defaultValue={
                    debitNote?.vendor?.name || debitNote?.vendor_name
                  }
                  readOnly
                />
              </div>

              <div className="form-row">
                <InputBoxLarge
                  label="Return Reference"
                  defaultValue={debitNote?.uid}
                  readOnly
                />
              </div>

              <div className="form-row">
                <DateTimeInputField
                  className="w-48"
                  label="Debit Note Date"
                  onChange={(e) =>
                    handleFieldChange("debit_note_date", e.target.value)
                  }
                  border="blue-border"
                  error={formErrors?.debit_note_date}
                  important
                />
                <SearchSelectBox
                  className="w-48"
                  label="Journal"
                  options={journals}
                  defaultValue={selectedJournal}
                  onChange={handleJournalChange}
                  placeholder="Select a debit note journal"
                  border="blue-border"
                  error={formErrors?.journal}
                  important
                />
              </div>
              <div className="form-row">
                <TextBox
                  label="Notes"
                  defaultValue={debitNote?.notes}
                  onChange={(e) => handleFieldChange("notes", e.target.value)}
                  border="blue-border"
                />
              </div>
            </div>
          </div>

          <div className="details-container" style={{ marginRight: "20px" }}>
            <div className="address-box" style={{ marginTop: "1.5rem" }}>
              <div className="form-column w-48">
                <div className="address-header">
                  <label className="address-label">
                    {t("Billing Address")}
                  </label>
                </div>
                <div className="sale-address-box" style={{ minHeight: "90%" }}>
                  <div className="customer-address">
                    {billAddress?.country ? (
                      <>
                        <span className="ftw-4 dk-gray">
                          {billAddress.street_1}
                        </span>
                        <span className="ftw-4 dk-gray">
                          {billAddress.street_2}
                        </span>
                        <span className="ftw-4 dk-gray">
                          {billAddress.city},{""}
                          {billAddress.state?.name || billAddress.state}
                        </span>
                        <span className="ftw-4 dk-gray">
                          {billAddress.country?.name || billAddress.country}
                        </span>
                        <span className="ftw-4 dk-gray">
                          {billAddress.zipcode}
                        </span>
                      </>
                    ) : (
                      <span className="ftw-4 red"></span>
                    )}
                  </div>
                </div>
              </div>

              <div className="address-separation"></div>

              <div className="form-column w-48">
                <div className="address-header">
                  <label className="address-label">
                    {t("Shipping Address")}
                  </label>
                </div>
                <div className="sale-address-box" style={{ minHeight: "90%" }}>
                  <div className="customer-address">
                    {shipAddress?.country ? (
                      <>
                        <span className="ftw-4 dk-gray">
                          {shipAddress.street_1}
                        </span>
                        <span className="ftw-4 dk-gray">
                          {shipAddress.street_2}
                        </span>
                        <span className="ftw-4 dk-gray">
                          {shipAddress.city},{""}
                          {shipAddress.state?.name || shipAddress.state}
                        </span>
                        <span className="ftw-4 dk-gray">
                          {shipAddress.country?.name || shipAddress.country}
                        </span>
                        <span className="ftw-4 dk-gray">
                          {shipAddress.zipcode}
                        </span>
                      </>
                    ) : (
                      <span className="ftw-4 red"></span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="products-container ml-13">
          <div
            className="ftw-5 common-text dk-gray"
            style={{ paddingLeft: "0.5rem" }}
          >
            {t("Order Lines")}
          </div>

          <table
            className="common-table no-hover-table"
            style={{ width: "100%" }}
          >
            <thead className="common-table-head">
              <tr className="common-table-thead-tr">
                <th className="uom-table-pad w-20">{t("Product")}</th>
                <th className="w-10">{t("Qty")}</th>
                <th className="w-10">{t("UOM")}</th>
                <th className="w-10">{t("Unit Price")}</th>
                <th className="w-13">{t("Discount")}</th>
                <th className="w-10">{t("Tax Rate")}</th>
                <th className="w-10">{t("Tax Amount")}</th>
                <th className="w-13">{t("Sub Total")}</th>
                <th className="w-2"></th>
              </tr>
            </thead>
            <tbody className="common-table-tbody compact-table-tbody">
              {products
                .filter((product) => product.product?.id || product.isNewRow)
                .map((product, index) => {
                  const remainingItem = remainingQuantity.find(
                    (item) => item?.product_id === product.product?.id,
                  );

                  const availableProductOptions =
                    getAvailableProductsForAdd().map((p) => ({
                      label: p.product.name,
                      value: p.product.id,
                    }));

                  return (
                    <tr key={index}>
                      <td className="uom-table-pad">
                        {product.isNewRow ? (
                          <SearchSelectBox
                            options={availableProductOptions}
                            placeholder="Select Product"
                            className="w-95"
                            onChange={(e) =>
                              handleProductSelection(index, e.target.value)
                            }
                          />
                        ) : (
                          <InputBox
                            defaultValue={product?.product?.name}
                            readOnly
                          />
                        )}
                      </td>
                      <td>
                        {!product.isNewRow && (
                          <>
                            <InputBox
                              defaultValue={product?.quantity}
                              border="blue-border"
                              onChange={(e) =>
                                handleProductChange(
                                  index,
                                  "quantity",
                                  e.target.value,
                                )
                              }
                              maxQuantity={remainingItem?.remaining_qty || 0}
                            />
                            {product.is_cw && (
                              <InputBox
                                label="CW Qty"
                                readonly
                                value={product.qty}
                              />
                            )}
                          </>
                        )}
                      </td>
                      <td>
                        {!product.isNewRow && (
                          <>
                            <InputBox
                              defaultValue={product.uom?.name || product.uom}
                              readOnly
                            />
                            {product.is_cw && (
                              <InputBox label="CW UOM" value={product.uom} />
                            )}
                          </>
                        )}
                      </td>
                      <td>
                        {!product.isNewRow && (
                          <InputBox
                            type="number"
                            defaultValue={product.unit_price}
                            readOnly
                          />
                        )}
                      </td>
                      <td>
                        {!product.isNewRow && (
                          <DiscountBox
                            options={discountOptions}
                            className="w-100"
                            defaultOption={product?.discount_type}
                            defaultValue={
                              product?.discount_amount ?? product?.discount
                            }
                            readOnly
                          />
                        )}
                      </td>
                      <td>
                        {!product.isNewRow && (
                          <InputTagsBoxSmall
                            defaultValue={
                              Array.isArray(product.applied_taxes)
                                ? product.applied_taxes
                                    .filter(
                                      (tax) =>
                                        typeof tax.tax_rate !== "undefined" &&
                                        tax.tax_rate !== null,
                                    )
                                    .map((tax) => `${tax.tax_rate}%`)
                                : []
                            }
                            readonly={true}
                          />
                        )}
                      </td>
                      <td>
                        {!product.isNewRow && (
                          <InputBox
                            defaultValue={product?.tax_amount || 0}
                            readOnly
                          />
                        )}
                      </td>
                      <td>
                        {!product.isNewRow && (
                          <InputBox
                            type="number"
                            defaultValue={product.subtotal}
                            readOnly
                          />
                        )}
                      </td>
                      <td>
                        <CloseRed onClick={() => removeProduct(index)} />
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
          {canAddMoreProducts && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginRight: "1rem",
                marginTop: "1rem",
              }}
            >
              <AddButtonTable
                imageSrc={"assets/plus-yellow.png"}
                buttonText={t("Add Product Line")}
                onClick={addNewProduct}
                bgColor="#094C80"
                textColor="#FFFFFF"
              />
            </div>
          )}
        </div>

        <div className="price-container fl-row-2 ml-13">
          <div className="terms-condition bdr-grey" style={{ width: "40%" }}>
            <TextBox
              label="Terms and Conditions"
              defaultValue={debitNote?.terms_conditions}
              readOnly
            />
          </div>

          <div className="discount details bdr-grey">
            <div className="fl-col">
              <div
                className="fl-row-2 ftw-4 common-text dk-gray ai-center"
                style={{ justifyContent: "space-between" }}
              >
                <div className="discount-icon-wrapper">
                  <span>{t("Discount")}</span>
                  <QuestionMarkIcon className="question-icon" />
                </div>
                <span>
                  {debitNote?.discount_percentage
                    ? `${debitNote.discount_percentage} %`
                    : debitNote?.discount_amount
                      ? `${currencySymbol} ${debitNote.discount_amount}`
                      : "-"}
                </span>
              </div>
            </div>
          </div>

          <div
            className="total details summary-details-section bdr-grey"
            style={{ marginRight: "20px" }}
          >
            <div className="fl-col">
              <div
                className="fl-row-2 ftw-4 common-text dk-gray"
                style={{ justifyContent: "space-between" }}
              >
                <span>{t("Gross Total")}</span>
                <span>{debitNote?.gross_total || 0}</span>
              </div>
              <div
                className="fl-row-2 ftw-4 common-text dk-gray"
                style={{ justifyContent: "space-between" }}
              >
                <span>{t("Discount Amount")}</span>
                <span>{debitNote?.discount_amount || 0}</span>
              </div>
              <div
                className="fl-row-2 ftw-4 common-text dk-gray"
                style={{ justifyContent: "space-between" }}
              >
                <span>{t("Untaxed Total")}</span>
                <span>{debitNote?.untaxed_total || 0}</span>
              </div>
              <div
                className="fl-row-2 ftw-4 common-text dk-gray"
                style={{ justifyContent: "space-between" }}
              >
                <span>{t("Taxable Amount")}</span>
                <span>{debitNote?.tax_amount || 0}</span>
              </div>
              <div
                className="fl-row-2 ftw-4 common-text dk-gray"
                style={{ justifyContent: "space-between" }}
              >
                <span>{t("Total Amount")}</span>
                <span>{debitNote?.total_amount || 0}</span>
              </div>
              {/* <div className="fl-row-2 ftw-6 common-text dk-gray" 
                                 style={{ justifyContent: "space-between" }}>
                                <span>Adjustment Amount</span>
                                <span>
                                    {debitNote?.adjustment_type || "+"} {debitNote?.adjustment_amount || 0}
                                </span>
                            </div> */}
            </div>
          </div>
        </div>

        <div
          className="total-container fl-row-2 ml-13"
          style={{ marginRight: "20px" }}
        >
          <div className="total-banner net-total-display common-text ftw-5 fl-row-1"></div>
          <div
            className="net-total-display fl-row-2 common-text ftw-5"
            style={{ justifyContent: "space-between" }}
          >
            <span>{t("Net Total Amount")}</span>
            <span>
              {currencySymbol} {debitNote?.net_total_amount || 0}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateDebitNotes;
